
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';

interface AppState {
  selectedViewUser: string | null;
  setSelectedViewUser: (userId: string | null) => void;
  
  selectedOrganization: string | null;
  setSelectedOrganization: (organization: string | null) => void;
  
  lastSelectedOrganization: string | null;
  
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  
  principalUserId: string | null;
  setPrincipalUserId: (userId: string | null) => void;
}

const useAppStateStore = create<AppState>()(
  persist(
    (set) => ({
      selectedViewUser: null,
      setSelectedViewUser: (userId) => set({ selectedViewUser: userId }),
      
      selectedOrganization: null,
      setSelectedOrganization: (organization) => {
        console.log(`Setting selected organization in useAppState: ${organization}`);
        // Don't update state if the value is the same to prevent infinite loops
        return set((state) => {
          if (state.selectedOrganization === organization) {
            return state; // Return current state unchanged if value is the same
          }
          return { 
            selectedOrganization: organization,
            lastSelectedOrganization: organization || null
          };
        });
      },
      
      lastSelectedOrganization: null,
      
      isMobileMenuOpen: false,
      toggleMobileMenu: () => set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen })),
      
      principalUserId: null,
      setPrincipalUserId: (userId) => set({ principalUserId: userId }),
    }),
    {
      name: 'app-state',
      partialize: (state) => ({ 
        selectedViewUser: state.selectedViewUser,
        selectedOrganization: state.selectedOrganization,
        lastSelectedOrganization: state.lastSelectedOrganization
      }),
    }
  )
);

export const useAppState = () => {
  const store = useAppStateStore();
  const { user, isAdmin } = useAuth();
  
  // This effect runs once when the component mounts and
  // identifies the principal user for the current user's organization
  useEffect(() => {
    const fetchPrincipalUser = async () => {
      if (!user || !user.organization_name) return;
      
      try {
        // Check if current user is a secondary user
        const isSecondaryUser = user.role !== 'Usuario Principal' && user.role !== 'Administrador' && user.role !== 'Sub Administrador';
        
        // Use RPC to get the organization's principal user ID
        const { data: principalData, error } = await supabase.rpc(
          'get_organization_principal_user_id',
          { org_name: user.organization_name }
        );
        
        if (error) {
          console.error('Error fetching principal user:', error);
          return;
        }
        
        if (principalData) {
          console.log('Found principal user for organization:', principalData);
          store.setPrincipalUserId(principalData);
          
          // For secondary users, we always set the view to the principal user
          if (isSecondaryUser) {
            store.setSelectedViewUser(principalData);
          } else if (!isAdmin) {
            // If user is principal but not admin, they see their own data by default
            store.setSelectedViewUser(user.id);
          }
        }
      } catch (err) {
        console.error('Error in principal user fetch:', err);
      }
    };

    fetchPrincipalUser();
  }, [user, isAdmin]);

  return store;
};
